import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Buildings, CheckCircle, Gear, MagnifyingGlass, SlidersHorizontal } from '@phosphor-icons/react';
import { NAV } from '@/components/Layout';
import { CompanyFunctionRow } from '@/components/CompanyFunctionRow';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';
import { useAllFeatures } from '@/hooks/useFeature';

const GROUPS = NAV.map(group => ({ ...group, items: group.items.filter(item => !item.to.startsWith('/instellingen') && !item.superAdminOnly) }))
  .map(group => group.section === 'Communicatie' ? { ...group, items: [...group.items, { to: '/notificaties', label: 'Notificaties', icon: Bell }] } : group);
const ITEMS = GROUPS.flatMap(g => g.items);

export default function FunctiesPage() {
  const { data, error, saving, update, reload } = useCompanyFunctions();
  const legacy = useAllFeatures();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const active = item => data?.functies[item.to.slice(1)] !== false;
  const activeCount = ITEMS.filter(active).length;
  const groups = GROUPS.filter(g => category === 'all' || category === g.section).map(g => ({ ...g,
    items: g.items.filter(item => `${item.label} ${g.section}`.toLowerCase().includes(search.trim().toLowerCase()) &&
      (status === 'all' || (status === 'active' ? active(item) : !active(item)))) }));

  return <div className="max-w-5xl mx-auto min-w-0 pb-16" data-testid="company-functions-page">
    <Link to="/instellingen" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#002FA7] mb-5" data-testid="functions-back-settings">
      <ArrowLeft size={14} /> Instellingen
    </Link>
    <header className="flex items-start justify-between gap-4 flex-wrap mb-5">
      <div>
        <h1 className="page-heading" data-testid="functions-title">Functies</h1>
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 mt-2" data-testid="functions-company-scope"><Buildings size={16} /> Voor het hele bedrijf</span>
      </div>
      <Link to="/instellingen?tab=functie-details" className="btn btn-secondary btn-sm inline-flex items-center gap-2" data-testid="functions-advanced-link">
        <SlidersHorizontal size={15} /> Aanvullende functies
      </Link>
    </header>
    <p className="text-sm text-gray-600 leading-relaxed mb-5" data-testid="functions-visibility-notice">
      Deactief verbergt de functie uit het menu en de snelknoppen. Gegevens en rechtstreekse links blijven behouden.
      Bestaande toegangsrechten en aanvullende functie-instellingen blijven gelden.
    </p>
    {error && <div role="alert" className="bg-red-50 border border-red-200 p-3 mb-4 text-sm text-red-800" data-testid="functions-error">
      {error} <button type="button" onClick={reload} disabled={!!saving} className="underline font-semibold ml-1" data-testid="functions-retry">Opnieuw laden</button>
    </div>}
    <div className="border border-gray-200 mb-6" data-testid="functions-locked-section">
      <CompanyFunctionRow item={{ to: '/instellingen', label: 'Instellingen', icon: Gear }} active locked />
    </div>
    {!data ? <p role="status" className="text-sm text-gray-500" data-testid="functions-loading">{error ? 'Geen instellingen geladen.' : 'Functies laden…'}</p> : <>
      {!data.mag_wijzigen && <p className="text-sm text-amber-800 bg-amber-50 p-3 mb-4" data-testid="functions-readonly">Alleen een beheerder kan functies voor het bedrijf wijzigen.</p>}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-base font-semibold" data-testid="functions-navigation-heading">Menu & snelknoppen</h2>
        <span role="status" className="text-xs text-gray-500 inline-flex items-center gap-1.5" data-testid="functions-save-status">
          {saving ? 'Wijziging opslaan…' : error ? 'Niet bevestigd' : <><CheckCircle size={15} className="text-green-600" /> {data.bijgewerkt_op ? 'Wijzigingen opgeslagen' : 'Huidige instellingen'}</>}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="relative flex-1 min-w-[160px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-gray-400" />
          <input aria-label="Zoek een functie" value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek een functie…"
            className="field-input w-full !pl-9 !py-2.5" data-testid="functions-search" />
        </label>
        <select aria-label="Categorie" className="field-input !w-auto max-w-full" value={category} onChange={e => setCategory(e.target.value)} data-testid="functions-category">
          <option value="all">Alle categorieën</option>
          {GROUPS.map(g => <option key={g.section} value={g.section}>{g.section}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-5" role="group" aria-label="Statusfilter">
        {[['all', 'Alle', ITEMS.length], ['active', 'Actief', activeCount], ['inactive', 'Deactief', ITEMS.length - activeCount]].map(([key, label, count]) =>
          <button key={key} type="button" aria-pressed={status === key} onClick={() => setStatus(key)} data-testid={`functions-filter-${key}`}
            className={`px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors ${status === key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>
            {label} <span className="ml-1 tabular-nums" data-testid={`functions-count-${key}`}>{count}</span>
          </button>)}
      </div>
      {groups.map(g => !!g.items.length && <section key={g.section} className="mb-6" data-testid={`functions-group-${g.section.toLowerCase()}`}>
        <h2 className="text-base font-semibold text-gray-800 mb-2" data-testid={`functions-group-title-${g.section.toLowerCase()}`}>{g.section}</h2>
        <div className="border border-gray-200">
          {g.items.map(item => <CompanyFunctionRow key={item.to} item={item} active={active(item)} disabled={!!saving || !data.mag_wijzigen}
            saving={saving === item.to.slice(1)} restriction={active(item) && legacy?.[item.featureKey || item.instellingFlag] === false}
            onChange={value => update(item.to.slice(1), value)} />)}
        </div>
      </section>)}
      {!groups.some(g => g.items.length) && <p className="text-sm text-gray-500 py-8" data-testid="functions-empty">Geen functies gevonden met deze filters.</p>}
    </>}
  </div>;
}